import { Col, notification } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

import { matchPath, useHistory, useLocation, useParams } from 'react-router-dom';
import { useGlobalState } from './components/GlobalState';
import SelectLakeAndTickets from './components/SelectLakeAndTickets';
import SelectSwims from './components/Step2';
import Step3 from './components/Step3';

import UserBookingSideSummary from './components/summaries/UserBookingSideSummary';
import '../admin/components/bookings/assets/css/admin_booking.css';
import './assets/css/user_bookings.css';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { exotic_fishery } from '../common/Utils';
import UserBookingSummary from './components/summaries/UserBookingSummary';
import UserBookingPaymentPage from './components/payment_page/UserBookingPaymentPage';
import UserBookingSuccessPage from './components/booking_success/UserBookingSuccessPage';
import UserBookingFailurePage from './components/booking_success/UserBookingFailurePage';
import UserBookingNavButtons from './components/UserBookingNavButtons';
import SelectDatesAndTimes from './components/date_selection/SelectDatesAndTimes';
import { emptyDatesObject } from '../availability_calendar/AvailabilityCalendar';
import SvgCalendar from '../../assets/images/Icons/SVG/Calendar';
import LicenceInfo from './components/summaries/LicenceInfo';
import { costString, getSummaryCosts } from '../../utils/cost';

import { useGetAddOnsQuery, useGetCustomPropertiesQuery } from '../admin/components/bookings/bookingSlice';
import { useGetIsUserInBookingQueueQuery } from '../user_profile/userProfileSlice';
import dayjs from 'dayjs';
import { usePreventScroll } from '../../utils/hooks';
import QueueModal from './components/QueueModal';

export const USER_BOOKING_STEP_ORDER = [
  'lake',
  'dates',
  'swims',
  'anglers',
  'summary',
  'payment',
  'success',
  'failure',
];

const HideFishery = process.env.REACT_APP_FISHERY_PRICE_HIDE;

export function UserBookings({ fisheryId, inWidget = false }) {
  const [current, setCurrent] = useGlobalState('current');

  const [userSelectedLakeId] = useGlobalState('userSelectedLakeId');
  const [lakeDetails] = useGlobalState('lakeDetails');
  const [lakeExclusive] = useGlobalState('lakeExclusive');
  const [userSelectedSwims] = useGlobalState('userSelectedSwims');
  const [userSelectedDoubleSwim] = useGlobalState('userSelectedDoubleSwim');
  const [userSelectedAnglers] = useGlobalState('userSelectedAnglers');
  const [userSelectedRods] = useGlobalState('userSelectedRods');
  const [userSelectedPakage] = useGlobalState('userSelectedPakage');
  const [lakeType] = useGlobalState('lakeType');
  const [, setIsInsideWidget] = useGlobalState('isInsideWidget');
  const [, setSwimImages] = useState(null);
  const [customPropertyDetails, setCustomPropertyDetails] = useGlobalState(
    'customPropertiesDetails'
  );
  const [, setSwimAvailableData] = useGlobalState('swimAvailableData');
  const [estimatedPricing, setEstimatedPricing] =
    useGlobalState('estimatedPricing');
  const [, setunSelectableSwimIds] = useGlobalState('unSelectableSwimIds');
  const [bookingDates, setBookingDates] = useGlobalState('bookingDates');
  const [, setAvailableTime] = useGlobalState('availableTime');

  const [arrivalTime, setArrivalTime] = useGlobalState('arrivalTime');
  const [departureTime, setDepartureTime] = useGlobalState('departureTime');
  const [, setDateText] = useGlobalState('dateText');
  const [bookingType] = useGlobalState('bookingType');

  const is_exotic_fishery = fisheryId === exotic_fishery;
  const [addedItems, setAddedItems] = useGlobalState('addedProductItem');
  const [, setCartItems] = useGlobalState('productItem');
  const { data: ProductsData } = useGetAddOnsQuery(
    {
      fisheryId: fisheryId,
      packageId: userSelectedPakage
        ? lakeDetails?.custom_slot_days[userSelectedPakage - 1]?.id
        : null,
      lakeId: userSelectedLakeId,
    },
    {
      skip: !fisheryId,
    }
  );
  const { data: userIsInQueueResult, refetch: checkQueue } =
    useGetIsUserInBookingQueueQuery(fisheryId, { skip: !fisheryId });
  const isElphicks =
    (fisheryId ?? '0') === process.env.REACT_APP_FISHERY_TIME_ID;
  const isLaunchDay = dayjs().isSame(new Date(2024, 0, 2), 'day');

  const userIsInQueue =
    userIsInQueueResult ??
    (isElphicks &&
      isLaunchDay); /* Default to true on launch day whilst fetching */
  usePreventScroll('BookingQueue', userIsInQueue);
  const { data: customProperties } = useGetCustomPropertiesQuery(
    {
      fisheryId: fisheryId,
      bookingType: lakeExclusive ? 'lake_exclusive' : bookingType,
      lakeId: userSelectedLakeId,
      online: true,
    },
    {
      skip: !fisheryId || !userSelectedLakeId,
    }
  );
  useEffect(() => {
    let updatedProperties = customProperties?.map((item) => {
      return {
        ...item,
        field_value: '',
      };
    });
    setCustomPropertyDetails(updatedProperties);
  }, [JSON.stringify(customProperties)]);
  const isFormValid = customPropertyDetails?.every(
    (item) => item.field_value && item.field_value.trim() !== ''
  );
  useEffect(() => {
    /* Check queue status at periodic intervals */
    if (!userIsInQueue) {
      return;
    }
    const timeoutSeconds = Math.floor(Math.random() * 30 + 90);
    const t = setTimeout(() => {
      checkQueue();
    }, timeoutSeconds * 1000);

    return () => {
      clearTimeout(t);
    };
  });
  useEffect(() => {
    setCartItems(ProductsData?.map((item) => ({ ...item, quantity: 0 })));
    setAddedItems([]);
  }, [ProductsData]);

  useEffect(() => {
    /* Update isInsideWidget state if prop value changes */
    setIsInsideWidget(inWidget);
  }, [inWidget, setIsInsideWidget]);

  const {
    total: estimatedTotal,
    advance: estimatedDeposit,
    isDiscounted: isEstimateDiscounted,
    discountedTotal: estimatedDiscountedTotal,
  } = getSummaryCosts(lakeExclusive, is_exotic_fishery, estimatedPricing);
  const estimatedPayableTotal = isEstimateDiscounted
    ? estimatedDiscountedTotal
    : estimatedTotal;

  // ensures that steps here match USER_BOOKING_STEP_ORDER
  const steps = useMemo(
    () =>
      USER_BOOKING_STEP_ORDER.map((key) =>
        [
          {
            key: 'lake',
            title: 'Select Lake',
            content: (
              <SelectLakeAndTickets
                fishery_id={fisheryId}
                disabled={userIsInQueue}
              />
            ),
          },
          {
            key: 'dates',
            title: 'Select Dates and Times',
            content: <SelectDatesAndTimes fishery_id={fisheryId} />,
          },
          {
            key: 'swims',
            title: 'Select Swims',
            content: <SelectSwims fishery_id={fisheryId} />,
          },
          {
            key: 'anglers',
            title: 'Info',
            content: <Step3 fishery_id={fisheryId} />,
          },
          {
            key: 'summary',
            title: 'Summary',
            content: <UserBookingSummary fisheryPublicId={fisheryId} />,
          },
          {
            key: 'payment',
            title: 'Payment Details',
            content: <UserBookingPaymentPage />,
          },
          {
            key: 'success',
            title: 'Payment Success',
            content: <UserBookingSuccessPage fisheryPublicId={fisheryId} />,
          },
          {
            key: 'failure',
            title: 'Payment Failure',
            content: <UserBookingFailurePage fisheryPublicId={fisheryId} />,
          },
        ].find((step) => step.key === key)
      ),
    [fisheryId, userIsInQueue]
  );

  const getSwimClickableEntries = (data) => {
    setunSelectableSwimIds([]);
    let _dates = [];
    let selectedDates = data.selected_dates;
    data.swims.forEach((swim) => {
      data.display_dates.forEach((date) => {
        if (
          data.booked_dates &&
          data.booked_dates[swim.id] &&
          data.booked_dates[swim.id].indexOf(date) > -1
        ) {
          if (selectedDates.indexOf(date) !== -1) {
            _dates.push(swim.id);
          }
        }
      });
    });

    const uniqueDates = [...new Set(_dates)];

    setunSelectableSwimIds(uniqueDates);
    // This will render next day
  };
  const resetSelections = () => {
    setAvailableTime(null);
    // Keeps the object reference invariant to prevent calendar re-render (weird bug with DateRangePicker)
    bookingDates[0] = { ...emptyDatesObject };
    setBookingDates(bookingDates);
    setArrivalTime(null);
    setDepartureTime(null);
    setEstimatedPricing(null);
    // setDateText(`${new Date()}reset`);
  };
  const getSwimAvailability = () => {
    let payload_data = {
      arrival_timestamp: arrivalTime,
      departure_timestamp: departureTime,
      lake_id: userSelectedLakeId,
      anglers: userSelectedAnglers,
      guest: userSelectedRods,
      swims: [],
      is_mobile_device: window.innerWidth <= 700,
      book_lake_exclusive: lakeExclusive,
      booking_type: bookingType,
      ...(userSelectedPakage && {
        custom_slot_id:
          lakeDetails?.custom_slot_days[userSelectedPakage - 1]?.id,
      }),
    };
    const axios_config = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    };
    axios
      .post('booking/swim_ticket/availability', payload_data, axios_config)
      .then((res) => {
        if (res.status === 200) {
          let res_data = res.data.result;
          if (
            lakeType.toLowerCase() !== 'ticket' &&
            bookingType.toLowerCase() === 'swim'
          ) {
            getSwimClickableEntries(res_data);
          }
          setSwimAvailableData(res_data);
          setSwimImages(res_data.images);

          if (
            lakeType.toLowerCase() === 'ticket' ||
            bookingType === 'ticket' ||
            lakeExclusive
          ) {
            setCurrent(USER_BOOKING_STEP_ORDER.indexOf('anglers'));
          } else {
            setCurrent(USER_BOOKING_STEP_ORDER.indexOf('swims'));
          }
        }
      })
      .catch((err) => {
        console.error(err);
        if (err.response) {
          if (err.response.status === 500) {
            notification.error({
              message: <div className='gSemiBold'>There was an issue!</div>,
              style: { border: '1px #D34545 solid' },
              placement: window?.innerWidth <= 500 ? 'bottom' : 'topRight',
              className: 'gRegular br10',
              description: 'Server Response failed. Please try again later',
            });
          } else if (err.response.status === 401) {
            notification.error({
              message: <div className='gSemiBold'>There was an issue!</div>,
              style: { border: '1px #D34545 solid' },
              placement: window?.innerWidth <= 500 ? 'bottom' : 'topRight',
              className: 'gRegular br10',
              description:
                'Your action will not be saved. Please Login and try again!',
            });
          } else if (err.response.status === 400) {
            notification.error({
              message: <div className='gSemiBold'>There was an issue!</div>,
              style: { border: '1px #D34545 solid' },
              placement: window?.innerWidth <= 500 ? 'bottom' : 'topRight',
              className: 'gRegular br10',
              description: err.response.data['detail'],
            });
          } else if (err.response.status === 422) {
            if (err.response.data['detail'] === 'Signature has expired') {
              notification.error({
                message: <div className='gSemiBold'>There was an issue!</div>,
                style: { border: '1px #D34545 solid' },
                placement: window?.innerWidth <= 500 ? 'bottom' : 'topRight',
                className: 'gRegular br10',
                description:
                  'Login session has been expired. Please Login to continue!',
              });
            } else {
              notification.error({
                message: <div className='gSemiBold'>There was an issue!</div>,
                style: { border: '1px #D34545 solid' },
                placement: window?.innerWidth <= 500 ? 'bottom' : 'topRight',
                className: 'gRegular br10',
                description:
                  "OOPS! Server didn't like the request. If this persists please contact support team",
              });
            }
          } else if (err.response.status === 423) {
            notification.error({
              message: <div className='gSemiBold'>There was an issue!</div>,
              style: { border: '1px #D34545 solid' },
              placement: window?.innerWidth <= 500 ? 'bottom' : 'topRight',
              className: 'gRegular br10',
              description:
                'Sorry ! this booking slot has been booked by another user, Try different booking!',
            });
          }
          resetSelections();
        } else {
          notification.error({
            message: <div className='gSemiBold'>There was an issue!</div>,
            style: { border: '1px #D34545 solid' },
            placement: window?.innerWidth <= 500 ? 'bottom' : 'topRight',
            className: 'gRegular br10',
            description: 'Error processing result',
          });
          console.log(err);
        }
      })
      .finally(() => {});
  };

  const isNextDisabled = () => {
    return (
      userIsInQueue ||
      (current === USER_BOOKING_STEP_ORDER.indexOf('lake') &&
        (!userSelectedLakeId ||
          !userSelectedAnglers ||
          (lakeDetails?.lake_type === 'Swim-Ticket' && !bookingType) ||
          (lakeDetails?.custom_slot_booking && !userSelectedPakage))) ||
      (current === USER_BOOKING_STEP_ORDER.indexOf('dates') &&
        !(bookingDates && arrivalTime && departureTime)) ||
      (current === USER_BOOKING_STEP_ORDER.indexOf('swims') &&
        !(
          userSelectedSwims.length + userSelectedDoubleSwim.length >=
          userSelectedAnglers
        ))
    );
  };

  const next = () => {
    if (current === USER_BOOKING_STEP_ORDER.indexOf('lake')) {
      if (isNextDisabled()) {
        notification.error({
          message: <div className='gSemiBold'>There was an issue!</div>,
          style: { border: '1px #D34545 solid' },
          placement: window?.innerWidth <= 500 ? 'bottom' : 'topRight',
          className: 'gRegular br10',
          description: 'Please select all relevant fields to proceed',
        });
      } else {
        setCurrent(current + 1);
      }
    } else if (current === USER_BOOKING_STEP_ORDER.indexOf('dates')) {
      if (!isNextDisabled()) {
        getSwimAvailability();
      } else {
        if (!bookingDates?.[0]?.startDate) {
          notification.error({
            message: <div className='gSemiBold'>There was an issue!</div>,
            style: { border: '1px #D34545 solid' },
            placement: window?.innerWidth <= 500 ? 'bottom' : 'topRight',
            className: 'gRegular br10',
            description: 'Please select a date to proceed',
          });
        } else if (!arrivalTime) {
          notification.error({
            message: <div className='gSemiBold'>There was an issue!</div>,
            style: { border: '1px #D34545 solid' },
            placement: window?.innerWidth <= 500 ? 'bottom' : 'topRight',
            className: 'gRegular br10',
            description:
              'Please select an arrival and departure time to proceed',
          });
        } else if (!departureTime) {
          notification.error({
            message: <div className='gSemiBold'>There was an issue!</div>,
            style: { border: '1px #D34545 solid' },
            placement: window?.innerWidth <= 500 ? 'bottom' : 'topRight',
            className: 'gRegular br10',
            description: 'Please select a departure time to proceed',
          });
        } else {
          notification.error({
            message: <div className='gSemiBold'>There was an issue!</div>,
            style: { border: '1px #D34545 solid' },
            placement: window?.innerWidth <= 500 ? 'bottom' : 'topRight',
            className: 'gRegular br10',
            description: 'Please select all relevant fields to proceed',
          });
        }
      }
    } else {
      if (current === USER_BOOKING_STEP_ORDER.indexOf('swims')) {
        if (!isNextDisabled()) {
          setCurrent(current + 1);
        } else {
          notification.error({
            message: <div className='gSemiBold'>There was an issue!</div>,
            style: { border: '1px #D34545 solid' },
            placement: window?.innerWidth <= 500 ? 'bottom' : 'topRight',
            className: 'gRegular br10',
            description: is_exotic_fishery
              ? 'Please select at least one room for all guests'
              : `Please select at least 1 swim for all anglers`,
          });
        }
      } else {
        setCurrent(current + 1);
      }
    }
  };

  const prev = () => {
    if (
      current === USER_BOOKING_STEP_ORDER.indexOf('anglers') &&
      (lakeType.toLowerCase() === 'ticket' || lakeExclusive)
    ) {
      setCurrent(USER_BOOKING_STEP_ORDER.indexOf('dates')); // Skip 'swims' step
    } else {
      setCurrent(current - 1);
    }
  };

  const htmlHeader = (
    <Helmet>
      <meta charSet='utf-8' />
      <meta
        name='description'
        content='Book places to fish near you for free online via swimbooker. Our platform allows you to see live lake availability for carp & course fishing venues across the country.'
      />
      <title>Make a Booking | swimbooker</title>
    </Helmet>
  );

  const promotionAppliedMobileSummary = (
    <>
      {isEstimateDiscounted && (
        <p className='bg-white br5 fs-18 gSemiBold txt-center sb-blue pd-2 pd-left-15 pd-right-15 mg-top-4 mg-bottom-4 w-fit mg-auto-lr'>
          Promotion Applied: {costString(estimatedDiscountedTotal)}
        </p>
      )}
    </>
  );

  if (current < USER_BOOKING_STEP_ORDER.indexOf('summary')) {
    return (
      <>
        {htmlHeader}
        <div id='booking-outer-container'>
          <QueueModal open={userIsInQueue} fisheryPublicId={fisheryId} />
          <div id='booking-container'>
            <div id='booking-left-container'>
              <div className={'user-steps-content mg-bottom-20'}>
                {steps[current].content}
              </div>
              <UserBookingNavButtons
                showPrev={
                  current > USER_BOOKING_STEP_ORDER.indexOf('lake') &&
                  current < USER_BOOKING_STEP_ORDER.indexOf('anglers')
                }
                onPrev={prev}
                showNext={current < USER_BOOKING_STEP_ORDER.indexOf('anglers')}
                helpText={
                  current === USER_BOOKING_STEP_ORDER.indexOf('lake') ? (
                    <div className={'row-center gap-05r'}>
                      <SvgCalendar />
                      <span>Tap 'Continue' To Select Your Dates</span>
                    </div>
                  ) : undefined
                }
                onNext={next}
                nextProps={{
                  className: isNextDisabled() ? 'disabled' : 'enabled',
                }}
                pageKey={steps[current].key}
              />
            </div>

            <div id='booking-right-container'>
              <UserBookingSideSummary fisheryId={fisheryId} />
              {!is_exotic_fishery && <LicenceInfo fisheryId={fisheryId} />}
            </div>
          </div>
        </div>
        {fisheryId !== HideFishery && (
          <Col className='mobile-summary-total'>
            {lakeExclusive ? (
              <>
                <h2 className='white fs-20 gSemiBold mg-bottom-0'>
                  Total Price:
                  <span
                    className={
                      (isEstimateDiscounted ? ' txt-strikethrough ' : ' ') +
                      'pd-left-5'
                    }
                  >
                    {costString(estimatedTotal)}
                  </span>
                </h2>
                {promotionAppliedMobileSummary}
                {Boolean(estimatedDeposit) &&
                  estimatedDeposit < estimatedPayableTotal && (
                    <h2 className='white fs-20 gSemiBold mg-bottom-0'>
                      To Pay Today: {costString(estimatedDeposit)}
                    </h2>
                  )}
              </>
            ) : is_exotic_fishery ? (
              <>
                <h2 className='white fs-20 gSemiBold mg-bottom-0'>
                  50% Deposit Price:
                  <span
                    className={
                      (isEstimateDiscounted ? ' txt-strikethrough ' : ' ') +
                      'pd-left-5'
                    }
                  >
                    {costString(estimatedDeposit)}
                  </span>
                </h2>
                <h2 className='white fs-20 gSemiBold mg-bottom-0'>
                  Total Price: {costString(estimatedTotal)}
                </h2>
                {promotionAppliedMobileSummary}
              </>
            ) : (
              <>
                <h2 className='white fs-20 gSemiBold mg-bottom-0'>
                  {current < USER_BOOKING_STEP_ORDER.indexOf('anglers')
                    ? 'Estimated'
                    : 'Final'}{' '}
                  Price:
                  <span
                    className={
                      (isEstimateDiscounted ? ' txt-strikethrough ' : ' ') +
                      'pd-left-5'
                    }
                  >
                    {costString(estimatedTotal)}
                  </span>
                </h2>
                {promotionAppliedMobileSummary}
              </>
            )}
            {!isEstimateDiscounted && (
              <p className='white gLight fs-15 mg-bottom-0'>
                Calculated On Final Booking Step
              </p>
            )}
          </Col>
        )}
      </>
    );
  }

  // Summary or Payment pages
  return (
    <div className={'user_booking_outer_container'}>
      <div className={'user_booking_container--max-width bg-white'}>
        {htmlHeader}
        {steps[current].content}
      </div>
    </div>
  );
}

export default function UserBookingsPage() {
  const history = useHistory();
  const { pathname } = useLocation();
  const isNewBooking = matchPath(pathname, {
    path: '/booking/new',
    exact: false,
  });

  const fisheryId = useParams()['fishery_id'];
  const [, setCurrent] = useGlobalState('current');

  useEffect(() => {
    if (isNewBooking) {
      setCurrent(0);
      history.replace(`/booking/progress/${fisheryId}`);
    }
  }, [fisheryId, history, isNewBooking, setCurrent]);

  return <UserBookings fisheryId={fisheryId ? String(fisheryId) : null} />;
}
